/**
 * AI video models a user can pick to animate a frame. The key is what's
 * stored in configs; which vendor runs it is the API's business.
 */
export const VIDEO_MODELS = {
  kling: {
    label: 'Kling',
    description: 'Kling 3.0: lifelike motion and people, up to 4K',
  },
  seedance: {
    label: 'Seedance',
    description: 'Seedance 1.5 Pro: quick and cheaper, up to 1080p',
  },
} as const;

export type VideoModel = keyof typeof VIDEO_MODELS;

export const VIDEO_MODEL_IDS = Object.keys(VIDEO_MODELS) as [VideoModel, ...VideoModel[]];

/** Lengths, in seconds, every video model can make. */
export const VIDEO_DURATIONS = [5, 10] as const;
export type VideoDuration = (typeof VIDEO_DURATIONS)[number];

/** Shapes a scene video can come out in. */
export const VIDEO_FORMATS = {
  '9:16': { label: 'Vertical 9:16' },
  '1:1': { label: 'Square 1:1' },
  '16:9': { label: 'Landscape 16:9' },
} as const;
export type VideoFormat = keyof typeof VIDEO_FORMATS;
export const VIDEO_FORMAT_IDS = Object.keys(VIDEO_FORMATS) as [
  VideoFormat,
  ...VideoFormat[],
];

/** How sharp: the short side in pixels. 4K only on Kling. */
export const VIDEO_QUALITIES = {
  standard: { label: 'Standard 720p', short: 720, klingOnly: false },
  high: { label: 'High 1080p', short: 1080, klingOnly: false },
  '4k': { label: '4K', short: 2160, klingOnly: true },
} as const;
export type VideoQuality = keyof typeof VIDEO_QUALITIES;
export const VIDEO_QUALITY_IDS = Object.keys(VIDEO_QUALITIES) as [
  VideoQuality,
  ...VideoQuality[],
];

/** The exact frame a video comes out at, e.g. 9:16 high → 1080×1920. */
export function videoFrame(
  format: VideoFormat,
  quality: VideoQuality,
): { width: number; height: number } {
  const short = VIDEO_QUALITIES[quality].short;
  if (format === '1:1') return { width: short, height: short };
  const long = Math.round((short * 16) / 9);
  return format === '9:16'
    ? { width: short, height: long }
    : { width: long, height: short };
}

/** Why a model can't make a quality, or null when it can. */
export function qualityProblem(model: VideoModel, quality: VideoQuality): string | null {
  return VIDEO_QUALITIES[quality].klingOnly && model !== 'kling'
    ? '4K is Kling only - pick High or switch to Kling'
    : null;
}
